import React from 'react';
import { motion } from 'framer-motion';
import { Container, Row, Col } from 'reactstrap';
import { Link } from 'react-router-dom';

const SupportSection: React.FC = () => {
  const features = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      ),
      title: 'Real-Time Chat',
      subtitle: 'Instant Responses'
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
      ),
      title: 'Expert Guidance',
      subtitle: 'Certified Team'
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      ),
      title: 'Quick Resolution',
      subtitle: 'Under 1 Hour'
    }
  ];

  return (
    <section className="support-section">
      <Container>
        <Row className="align-items-center">
          {/* Left Content */}
          <Col lg={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="support-content"
            >
              <h2 className="support-title">
                We're here when
                <br />
                <span className="highlight">you need us most</span>
              </h2>
              <p className="support-subtitle">
                Questions? Issues? Our dedicated support team is available around the clock
                to help you succeed. No bots, just real people who care about your business.
              </p>

              {/* Features */}
              <div className="support-features">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="support-feature"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <div className="feature-icon">
                      {feature.icon}
                    </div>
                    <div className="feature-text">
                      <span className="feature-title">{feature.title}</span>
                      <span className="feature-subtitle">{feature.subtitle}</span>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA */}
              <div className="support-cta">
                <a href={`${process.env.REACT_APP_APP_URL || ''}/register`} className="cta-button">
                  Get Started
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </a>
                <span className="cta-note">14 day free trial • Cancel anytime</span>
              </div>
            </motion.div>
          </Col>

          {/* Right Side - Chat Mockup */}
          <Col lg={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="support-visual"
            >
              {/* Support Agent Image */}
              <div className="support-agent">
                <img src="/images/support-agent.png" alt="Support Agent" loading="lazy" />
              </div>

              {/* Chat Window */}
              <div className="chat-window">
                <div className="chat-header">
                  <div className="chat-header-icon">
                    <img src="/icon.png" alt="CodeRom" loading="lazy" />
                  </div>
                  <span className="chat-header-title">CodeRom Support</span>
                  <div className="chat-status">
                    <span className="status-dot"></span>
                    Online
                  </div>
                </div>

                <div className="chat-messages">
                  {/* Support Message */}
                  <div className="chat-message support">
                    <div className="message-avatar">
                      <img src="/icon.png" alt="Support" loading="lazy" />
                    </div>
                    <div className="message-content">
                      <p>Hi there! I'm here to help. What do you need assistance with?</p>
                      <span className="message-time">11:38 AM</span>
                    </div>
                  </div>

                  {/* User Message */}
                  <div className="chat-message user">
                    <div className="message-content">
                      <p>I have a question about product listing</p>
                    </div>
                  </div>

                  {/* Support Reply */}
                  <div className="chat-message support">
                    <div className="message-avatar">
                      <img src="/icon.png" alt="Support" loading="lazy" />
                    </div>
                    <div className="message-content">
                      <p>Of course! I'd be happy to help you with that.</p>
                      <span className="message-time">11:40 AM</span>
                    </div>
                  </div>
                </div>

                <div className="chat-input">
                  <input type="text" placeholder="Write a message..." readOnly />
                  <button className="send-btn">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="deco-icon deco-1">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <div className="deco-icon deco-2">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
              </div>
              <div className="deco-icon deco-3">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" fill="none"/>
                </svg>
              </div>
            </motion.div>
          </Col>
        </Row>
      </Container>

      <style>{`
        .support-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #ffffff 100%);
          position: relative;
          overflow: hidden;
        }

        .support-content {
          padding-right: 40px;
        }

        .support-title {
          font-size: 42px;
          font-weight: 800;
          color: #1f2937;
          line-height: 1.2;
          margin-bottom: 20px;
        }

        .support-title .highlight {
          background: linear-gradient(135deg, #f97316, #fb923c);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .support-subtitle {
          font-size: 18px;
          color: #6b7280;
          line-height: 1.7;
          margin-bottom: 32px;
        }

        .support-features {
          display: flex;
          gap: 24px;
          margin-bottom: 32px;
          flex-wrap: wrap;
        }

        .support-feature {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .feature-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #fff7ed, #ffedd5);
          border-radius: 12px;
          color: #f97316;
        }

        .feature-text {
          display: flex;
          flex-direction: column;
        }

        .feature-title {
          font-size: 15px;
          font-weight: 700;
          color: #1f2937;
        }

        .feature-subtitle {
          font-size: 14px;
          color: #6b7280;
        }

        .support-cta {
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .cta-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: white;
          text-decoration: none;
          font-size: 16px;
          font-weight: 600;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .cta-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(249, 115, 22, 0.4);
          color: white;
        }

        .cta-note {
          font-size: 14px;
          color: #6b7280;
        }

        /* Chat Window Styles */
        .support-visual {
          position: relative;
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;
          min-height: 450px;
        }

        .support-agent {
          position: absolute;
          right: 0;
          bottom: 0;
          z-index: 10;
        }

        .support-agent img {
          max-height: 420px;
          width: auto;
          object-fit: contain;
        }

        .chat-window {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 340px;
          background: white;
          border-radius: 20px;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.12);
          overflow: hidden;
        }

        .chat-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
        }

        .chat-header-icon {
          width: 36px;
          height: 36px;
          background: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .chat-header-icon img {
          width: 24px;
          height: 24px;
        }

        .chat-header-title {
          font-weight: 600;
          font-size: 16px;
          flex: 1;
        }

        .chat-status {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          opacity: 0.9;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          background: #4ade80;
          border-radius: 50%;
        }

        .chat-messages {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          min-height: 250px;
        }

        .chat-message {
          display: flex;
          gap: 10px;
          max-width: 85%;
        }

        .chat-message.support {
          align-self: flex-start;
        }

        .chat-message.user {
          align-self: flex-end;
        }

        .message-avatar {
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .message-avatar img {
          width: 20px;
          height: 20px;
        }

        .message-content {
          background: #f3f4f6;
          padding: 12px 16px;
          border-radius: 16px;
          border-top-left-radius: 4px;
        }

        .chat-message.user .message-content {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          border-radius: 16px;
          border-top-right-radius: 4px;
        }

        .message-content p {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
        }

        .message-time {
          display: block;
          font-size: 11px;
          color: #9ca3af;
          margin-top: 4px;
        }

        .chat-input {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 20px;
          border-top: 1px solid #f3f4f6;
        }

        .chat-input input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 14px;
          color: #6b7280;
        }

        .send-btn {
          width: 36px;
          height: 36px;
          border: none;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Decorative Icons */
        .deco-icon {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          padding: 10px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .deco-1 {
          top: 10%;
          right: 5%;
          background: linear-gradient(135deg, #fef3c7, #fde68a);
          color: #f59e0b;
        }

        .deco-2 {
          top: 50%;
          right: -5%;
          background: linear-gradient(135deg, #fce7f3, #fbcfe8);
          color: #ec4899;
        }

        .deco-3 {
          bottom: 25%;
          right: 8%;
          background: linear-gradient(135deg, #d1fae5, #a7f3d0);
          color: #10b981;
        }

        @media (max-width: 991px) {
          .support-section {
            padding: 60px 0;
          }

          .support-content {
            padding-right: 0;
            margin-bottom: 40px;
          }

          .support-title {
            font-size: 32px;
          }

          .support-features {
            gap: 16px;
          }

          .support-feature {
            flex: 1 1 calc(50% - 8px);
          }

          .support-visual {
            justify-content: center;
            min-height: 400px;
          }

          .support-agent {
            right: -20px;
          }

          .support-agent img {
            max-height: 350px;
          }

          .chat-window {
            max-width: 300px;
          }
        }

        @media (max-width: 576px) {
          .support-title {
            font-size: 28px;
          }

          .support-feature {
            flex: 1 1 100%;
          }

          .support-cta {
            flex-direction: column;
            align-items: flex-start;
          }

          .support-visual {
            min-height: 350px;
          }

          .support-agent {
            display: none;
          }

          .chat-window {
            max-width: 100%;
          }
        }
      `}</style>
    </section>
  );
};

export default SupportSection;
